import { getApiBaseUrl } from './base-url';

export class ApiError extends Error {
  status: number | undefined;
  code: string | number | undefined;
  error: string;
  url: string;

  constructor(message: string, code: string, url: string, status?: number) {
    super(message);
    this.error = message;
    this.code = code;
    this.url = url;
    this.status = status;
  }
}

function isJsonString(str: string) {
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
}

export function fetcher<T>(
  baseUrl: string | undefined,
  endpoint: string,
  config: RequestInit,
): Promise<T> {
  return fetch(`${baseUrl}${endpoint}`, config)
    .catch(async (networkError: unknown) => {
      if (typeof window !== 'undefined') {
        let maintenance = false;
        try {
          const res = await fetch('/api/maintenance-windows');
          if (res.status === 503) {
            maintenance = true;
          } else if (res.ok) {
            const windows = await res.json();
            maintenance = Array.isArray(windows) && windows.length > 0;
          }
          // any other non-ok status: fail open — don't redirect
        } catch {
          // oracle unreachable: fail open — don't redirect
        }
        if (maintenance) {
          window.location.replace('/maintenance');
          return new Promise<Response>(() => {});
        }
      }
      throw networkError;
    })
    .then(async (response) => {
      const responseText = await response.text();
      if (response.ok) {
        try {
          return (
            responseText
              ? isJsonString(responseText)
                ? JSON.parse(responseText)
                : responseText
              : {}
          ) as Promise<T>;
        } catch {
          // parse failed, fall through to error handling
        }
      }

      if (response.status === 503 && typeof window !== 'undefined') {
        window.location.replace('/maintenance');

        return new Promise<T>(() => {});
      }

      let errorMessage = '';
      let errorCode = '';

      if (responseText) {
        try {
          const errorValue = JSON.parse(responseText);

          console.warn(`Api Client =>`, `${baseUrl}${endpoint}`, errorValue);

          if (typeof errorValue === 'string') {
            errorMessage = errorValue;
          } else if (Array.isArray(errorValue)) {
            const [errorData] = errorValue;

            errorMessage = errorData?.message || errorData?.Message;
            errorCode =
              errorData?.errorCode || errorData?.code || errorData?.ErrorCode;
          } else {
            const resultError = Array.isArray(errorValue?.errors)
              ? errorValue.errors.find(
                  (entry: unknown) =>
                    entry !== null && typeof entry === 'object',
                )
              : undefined;
            errorMessage =
              errorValue?.message ||
              errorValue?.Message ||
              resultError?.message ||
              resultError?.Message ||
              errorValue?.title ||
              '';
            errorCode =
              resultError?.errorCode ||
              resultError?.code ||
              resultError?.ErrorCode ||
              '';
          }
        } catch (e) {
          console.warn(`Api Client =>`, `${baseUrl}${endpoint}`, e);
        }
      }

      throw new ApiError(
        errorMessage,
        errorCode,
        response.url,
        response.status,
      );
    });
}

/**
 * Config accepted by the api-client helpers.
 *
 * `body` is deliberately wider than `RequestInit['body']`: callers pass a plain
 * JSON-serializable value and `buildConfig` stringifies it. Only when
 * `jsonContent` is turned off is the value forwarded to `fetch` untouched, in
 * which case it has to be a real `BodyInit` (e.g. the `FormData` uploads in
 * `api/auth.ts` and `api/placements-qualification-flow.ts`).
 */
export type RequestInitWithJsonContent = Omit<RequestInit, 'body'> & {
  jsonContent?: boolean;
  body?: unknown;
};

type BaseClient<T = unknown> = (
  endpoint: string,
  customConfig: RequestInitWithJsonContent,
) => Promise<T>;

export function buildApiClientHelpers(baseClient: BaseClient) {
  return {
    get<T>(
      endpoint: string,
      config: RequestInitWithJsonContent = {},
    ): Promise<T> {
      config.method = 'GET';
      return baseClient(endpoint, config) as Promise<T>;
    },
    delete<T>(
      endpoint: string,
      config: RequestInitWithJsonContent = {},
    ): Promise<T> {
      config.method = 'DELETE';
      return baseClient(endpoint, config) as Promise<T>;
    },
    head<T>(
      endpoint: string,
      config: RequestInitWithJsonContent = {},
    ): Promise<T> {
      config.method = 'HEAD';
      return baseClient(endpoint, config) as Promise<T>;
    },
    post<T>(
      endpoint: string,
      body?: unknown,
      config: RequestInitWithJsonContent = {},
    ): Promise<T> {
      config.method = 'POST';
      config.body = body;
      return baseClient(endpoint, config) as Promise<T>;
    },
    put<T>(
      endpoint: string,
      body?: unknown,
      config: RequestInitWithJsonContent = {},
    ): Promise<T> {
      config.method = 'PUT';
      config.body = body;
      return baseClient(endpoint, config) as Promise<T>;
    },
  };
}

export function buildConfig(
  { body, jsonContent = true, ...customConfig }: RequestInitWithJsonContent,
  jwtToken?: string,
  trackingId?: string,
): RequestInit {
  const headers: Record<string, string> = { sourcetype: 'Consumer' };

  if (jwtToken) {
    headers.authorization = `Bearer ${jwtToken}`;
  }

  if (trackingId) {
    headers.trackingId = trackingId;
  }

  if (jsonContent) {
    headers['content-type'] = 'application/json';
  }

  const config: RequestInit = {
    ...customConfig,
    headers: {
      ...headers,
      ...customConfig.headers,
    },
  };

  if (body) {
    // With `jsonContent` off the caller has already supplied a real `BodyInit`
    // (e.g. `FormData`), so it is forwarded to `fetch` untouched.
    config.body = jsonContent ? JSON.stringify(body) : (body as BodyInit);
  }

  return config;
}

export function buildApiClient(jwtToken?: string, trackingId?: string) {
  function baseClient<T>(
    endpoint: string,
    customConfig: RequestInitWithJsonContent = {},
  ): Promise<T> {
    const config = buildConfig(customConfig, jwtToken, trackingId);
    const baseUrl = getApiBaseUrl();
    return fetcher<T>(baseUrl, endpoint, config);
  }

  return buildApiClientHelpers(baseClient);
}

export function buildOctaneClient(jwtToken?: string, trackingId?: string) {
  function baseClient<T>(
    endpoint: string,
    customConfig: RequestInitWithJsonContent = {},
  ): Promise<T> {
    const config = buildConfig(customConfig, jwtToken, trackingId);
    return fetcher<T>(process.env.NEXT_PUBLIC_OCTANE_URL, endpoint, config);
  }

  return buildApiClientHelpers(baseClient);
}
